import { usePresence } from "@roomservice/react";
import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import { ROOM_SERVICE_PRESENCE_KEYS } from "../consts";
import { currentSpaceAtom, onlinesAtom } from "../jotai/atoms";
import { Point2D, UserOnlines, Viewport } from "../types";
import equals from "fast-deep-equal";
import { useUpdateAtom } from "jotai/utils";

// In order to brute force fix RS presence boolean types occasionally
// disappearing/not respecting expiration time, we can also factor in
// the position presence, which is a good proxy for if someone is still
// online, and updates often
// this hook should only be used once to set up / handle all merging
// and saves it to an atom which any file that wants to know who's
// online can reference (useOnlines in atoms.tsx)
export function useOnlinesSetup() {
  const setOnlinesAtom = useUpdateAtom(onlinesAtom);

  const [onlines, client] = usePresenceWrapper<boolean>(
    ROOM_SERVICE_PRESENCE_KEYS.ONLINE
  );
  const [positions] = usePositions();
  const [onlinesAndPositions, set] = useState<UserOnlines>([]);

  // Positions change very frequently, which makes them a helpful
  // proxy to tell who is still online, but it can cause a lot of
  // rerenders as this effect will run every time someone's mouse
  // moves. In order to prevent setState from running too frequently
  // we'll check if the prior onlines and newly calculated onlines
  // are the same, and set to the same array if they are (arrays are refs in JS)
  useEffect(() => {
    const next = Object.keys({ ...positions, ...onlines }); // Merge onlines and positions
    set((prev) => (equals(prev, next) ? prev : next)); // only update if changed
  }, [onlines, positions, set]);

  // use an atom to cache the merged onlines and positions
  useEffect(() => {
    setOnlinesAtom(onlinesAndPositions);
  }, [onlinesAndPositions, setOnlinesAtom]);

  return [onlinesAndPositions, client] as const;
}

export function usePositions() {
  return usePresenceWrapper<Point2D>(ROOM_SERVICE_PRESENCE_KEYS.POSITION);
}
export function useViewports() {
  return usePresenceWrapper<Viewport>(ROOM_SERVICE_PRESENCE_KEYS.VIEWPORT);
}
export function usePreviews() {
  return usePresenceWrapper<Viewport>(ROOM_SERVICE_PRESENCE_KEYS.PREVIEW);
}

function usePresenceWrapper<T>(key: ROOM_SERVICE_PRESENCE_KEYS) {
  const [currentSpace] = useAtom(currentSpaceAtom);
  return usePresence<T>(currentSpace!.id, key);
}
